/**
 * useArTableContinuingBundleId
 *
 * Supplies `resolveArBundleDisplay`'s `continuingBundleId` from `ArTableBody`:
 * one probe read past the page's last document, since owning the last row is no
 * proof the bundle continues (SYD-2430).
 *
 * Two orderings and one bound the code cannot state for itself:
 * - The cursor is the persist's last query snapshot, which `persistCollection`
 *   assigns BEFORE it mutates `state.docs`, so a render that observed a page's
 *   docs can never see an older snapshot. `paginationCursors.byPage` is written
 *   from a `subscribe` attached in a passive effect and holds no entry at all on
 *   the page-turn commit.
 * - The key is the page's last row DOCID, never the page NUMBER. `StoreProvider`
 *   swaps the rows store in a parent effect and React runs child effects first,
 *   so the page-turn commit still holds the old page's docs, and a bundle
 *   spanning three pages ends both pages with the same bundleId — a page-number
 *   key would never re-probe and the misfiled answer would be permanent.
 * - The answer is one-shot on THIS page's window, so nothing on the NEXT page
 *   invalidates it: delete the continuation's members from another tab and this
 *   page keeps its marker until its own window changes. The marker is a
 *   page-turn observation, not a live fact.
 */

import { useEffect, useRef, useState } from 'react';
import {
  collection,
  getDocs,
  limit,
  orderBy as firestoreOrderBy,
  query,
  startAfter,
} from 'firebase/firestore';
import { firestore } from '@/services/startFirestore';
import { useStoreNonReactive } from '@/store';
import type { CollectionPersist } from '@/store';
import type { DocEntry, OrderByOption } from '@/store/Store.types';
import { queryOrderByKey } from '@/store/core/queryOrderByKey';
import { AR_TABLE_AGGREGATIONS_ROW_ID } from '#types';
import type { ArRowData, ArRowId } from '#types';

type UseArTableContinuingBundleIdParams = {
  rowsPath: string;
  rowsOrderBy: OrderByOption;
  /** The page in query order, un-narrowed — `visibleRowIds`, never `filteredRowIds`. */
  pageRowIds: readonly ArRowId[];
  /** `null` when bundles are off; the hook then probes nothing. */
  rowsDocs: ReadonlyMap<string, DocEntry<ArRowData>> | null;
  /** Nothing to continue onto, or nothing settled to probe from. */
  enabled: boolean;
};

function useArTableContinuingBundleId({
  rowsPath,
  rowsOrderBy,
  pageRowIds,
  rowsDocs,
  enabled,
}: UseArTableContinuingBundleIdParams): string | undefined {
  const rowsPersist = useStoreNonReactive('rows').persist as CollectionPersist<ArRowData>;
  /**
   * Read at probe time, never subscribed to: `probeKey` already names every
   * legitimate trigger, and a store swap clears the docs first, so the key moves
   * to `null` and back on its own. A dep here would only buy a duplicate read.
   */
  const rowsPersistRef = useRef(rowsPersist);
  rowsPersistRef.current = rowsPersist;

  const lastRowId = pageRowIds[pageRowIds.length - 1];
  const lastRow = rowsDocs && lastRowId !== undefined
    ? rowsDocs.get(lastRowId)?.data
    : undefined;
  const edgeBundleId = lastRow
    && (lastRow.bundleRole === 'header' || lastRow.bundleRole === 'member')
    ? lastRow.bundleId
    : undefined;

  const { field: orderByField, direction: orderByDirection } = rowsOrderBy;
  const orderByKey = queryOrderByKey(rowsOrderBy);
  /**
   * The question this hook is currently answering. Every outcome is filed under
   * it — continuation, no continuation, and a rejection — and the return derives
   * from a match, so a moved key reads as "not yet known" and a re-probe of an
   * unchanged key retracts what the data lost, with no effect resetting state.
   */
  const probeKey = enabled && lastRowId !== undefined && edgeBundleId !== undefined
    ? `${rowsPath}|${orderByKey}|${lastRowId}|${edgeBundleId}`
    : null;

  const [answer, setAnswer] = useState<{
    probeKey: string;
    bundleId: string | undefined;
  } | null>(null);

  useEffect(() => {
    if (probeKey === null || edgeBundleId === undefined) return undefined;
    const persist = rowsPersistRef.current;
    /** Same guard, same reason as SYD-2304: refuse a superseded ordering. */
    if (persist.orderByKey !== orderByKey) return undefined;

    const pageSnapshot = persist.getLastQuerySnapshot();
    const pageDocs = pageSnapshot?.docs.filter(
      (docSnapshot) => docSnapshot.id !== AR_TABLE_AGGREGATIONS_ROW_ID,
    ) ?? [];
    const lastDoc = pageDocs[pageDocs.length - 1];
    if (!lastDoc) return undefined;

    let cancelled = false;

    void (async () => {
      try {
        /**
         * Two documents, not one. Under a column sort on an aggregated column
         * the ordering field is `cells.<typeId>.value`, which the aggregations
         * row carries, so that row sits inside the limited results and a
         * `limit(1)` landing on it reports "does not continue" for a bundle that
         * does. `AR_TABLE_AGGREGATIONS_ROW_ID` is the only reserved docId a rows
         * collection holds and query results are distinct documents, so one
         * spare is exactly sufficient;
         * `functions/src/workers/getArTablePageCursor.ts` bounds the same read
         * the same way. The probed document never enters the page store, never
         * becomes a cursor, and is discarded.
         */
        const probe = await getDocs(query(
          collection(firestore, rowsPath),
          firestoreOrderBy(orderByField, orderByDirection),
          startAfter(lastDoc),
          limit(2),
        ));
        if (cancelled) return;
        const nextRow = probe.docs
          .find((docSnapshot) => docSnapshot.id !== AR_TABLE_AGGREGATIONS_ROW_ID)
          ?.data() as ArRowData | undefined;
        const continues = nextRow?.bundleRole === 'member'
          && nextRow.bundleId === edgeBundleId;
        setAnswer({ probeKey, bundleId: continues ? edgeBundleId : undefined });
      } catch (error) {
        console.error('ArTable bundle continuation probe failed:', error);
        if (cancelled) return;
        setAnswer({ probeKey, bundleId: undefined });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [edgeBundleId, orderByDirection, orderByField, orderByKey, probeKey, rowsPath]);

  return answer?.probeKey === probeKey ? answer.bundleId : undefined;
}

export { useArTableContinuingBundleId };
